import { apiClient as api } from "./httpClient";

// Agentes de IA da entidade (M2 — Comportamento). Só o Administrador gerencia (o backend valida
// via [HasPermission("ai-agents")] + EntityOwnershipGuard). Provedor/modelo/intensidade nulos =
// o agente herda a configuração da entidade (M1).

// Estágio do roteiro (M2 — pilar Roteiro). A ordem no array é a ordem da conversa guiada.
export interface AiAgentStage {
  name: string;
  objective?: string | null;
  instruction?: string | null;
  advanceCondition?: string | null;
}

export interface AiAgent {
  id: string;
  displayName: string;
  hasAvatar: boolean;
  internalDescription?: string | null;
  personalityTone?: string | null;
  systemPrompt: string;
  greetingMessage?: string | null;
  fallbackMessage?: string | null;
  integrationId?: string | null;
  model?: string | null;
  temperature?: number | null;
  reasoningEffort?: string | null;
  active: boolean;
  createdAt: string;
  targetProfileId?: string | null;
  dataScope: string; // "own" | "entity"
  /** Liberacao explicita para dado clinico (RN-555). Falso = ferramenta clinica nao e oferecida. */
  allowsClinicalData: boolean;
  /** Aparece na lista de conversas do chat (FAB e Conversas). Falso = só atende na área que o usa. */
  showInChat: boolean;
  /** Chaves das ferramentas LIGADAS neste agente (RN-556). Ferramenta nova nasce desligada. */
  enabledTools: string[];
  /** Áreas dos Assistentes que usam o agente — a aba Ferramentas abre no tipo da área. */
  areaKeys?: string[];
  knowledgeText?: string | null;
  /** Teto de respostas automáticas por conversa no canal (RN-618); nulo = padrão do sistema. */
  maxRepliesPerConversation?: number | null;
  /** Horas de silêncio a partir das quais a conversa recomeça para o agente (RN-620). */
  resumeWindowHours?: number | null;
  stages: AiAgentStage[];
}

// Perfil da entidade (dropdown de perfil-alvo do agente — Pilar 4).
export interface EntityProfile {
  id: string;
  name: string;
}

// Atribuição user×agente (M4): usuário da entidade + se está atribuído a este agente.
export interface AgentUser {
  userId: string;
  name: string | null;
  assigned: boolean;
}

// Ferramentas do agente (Pilar 4 / M3): catálogo + se está habilitada.
export interface AgentTool {
  key: string;
  label: string;
  description: string;
  sensitivity: string; // administrativo | identificacao | clinico_sensivel
  available: boolean;
  enabled: boolean;
  /** Tipo (agenda, livro-caixa, prontuario, pacientes, base-conhecimento, geral) — agrupa a aba. */
  kind: string;
  /** Ferramenta de área dos Assistentes (RN-572): só existe nas conversas daquela área */
  area?: string | null;
}

export interface AiAgentInput {
  displayName: string;
  internalDescription?: string | null;
  personalityTone?: string | null;
  systemPrompt: string;
  greetingMessage?: string | null;
  fallbackMessage?: string | null;
  integrationId?: string | null;
  model?: string | null;
  temperature?: number | null;
  reasoningEffort?: string | null;
  targetProfileId?: string | null;
  dataScope?: string | null; // "own" | "entity"
  allowsClinicalData?: boolean | null;
  showInChat?: boolean | null;
  /** Nulo ao criar = todas as ferramentas que existem hoje; nulo ao editar = mantém. */
  enabledTools?: string[] | null;
  knowledgeText?: string | null;
  maxRepliesPerConversation?: number | null;
  resumeWindowHours?: number | null;
  stages: AiAgentStage[];
}

/** Política de segurança da plataforma (D-OP-25): só leitura, vale para todo agente. */
export interface SafetyPolicyRule {
  code: string;
  text: string;
  /** Fala do sistema da clínica: não entra no prompt do WhatsApp. */
  systemOnly?: boolean;
}

export interface SafetyPolicy {
  version: string;
  rules: SafetyPolicyRule[];
  byKind: Record<string, SafetyPolicyRule[]>;
}

export const aiAgentsService = {
  getSafetyPolicy: async (): Promise<SafetyPolicy> => {
    const response = await api.get("/ai-agents/safety-policy");
    return response.data;
  },

  getProfiles: async (): Promise<EntityProfile[]> => {
    const response = await api.get("/ai-agents/profiles");
    return response.data;
  },

  getAgents: async (entityId: string, includeInactive = false): Promise<AiAgent[]> => {
    const response = await api.get(`/ai-agents/by-entity/${entityId}`, {
      params: { includeInactive },
    });
    return response.data;
  },

  create: async (entityId: string, input: AiAgentInput): Promise<string> => {
    const response = await api.post("/ai-agents", { entityId, ...input });
    return response.data.id as string;
  },

  update: async (id: string, input: AiAgentInput): Promise<void> => {
    await api.put(`/ai-agents/${id}`, input);
  },

  setActive: async (id: string, active: boolean): Promise<void> => {
    await api.patch(`/ai-agents/${id}/active`, { active });
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/ai-agents/${id}`);
  },

  // Avatar (MinIO via proxy autenticado, mesmo padrão da foto do paciente). O <img> não manda
  // header de auth → buscamos como blob e devolvemos um object URL (revogar ao trocar/desmontar).
  getAvatar: async (id: string): Promise<string> => {
    const response = await api.get(`/ai-agents/${id}/avatar`, { responseType: "blob" });
    return URL.createObjectURL(response.data);
  },

  // O apiClient tem Content-Type application/json default e ele VAZA para o FormData (a API dá
  // 415). Anulando o header, o browser define "multipart/form-data; boundary=..." sozinho.
  setAvatar: async (id: string, blob: Blob): Promise<void> => {
    const form = new FormData();
    form.append("file", blob, "avatar.jpg");
    await api.post(`/ai-agents/${id}/avatar`, form, { headers: { "Content-Type": null } });
  },

  removeAvatar: async (id: string): Promise<void> => {
    await api.delete(`/ai-agents/${id}/avatar`);
  },

  // Atribuição (M4): usuários da entidade com flag de atribuído; salvar substitui o conjunto.
  getUsers: async (agentId: string): Promise<AgentUser[]> => {
    const response = await api.get(`/ai-agents/${agentId}/users`);
    return response.data;
  },

  setUsers: async (agentId: string, userIds: string[]): Promise<void> => {
    await api.put(`/ai-agents/${agentId}/users`, { userIds });
  },

  // Ferramentas (RN-556): catálogo com o teto do perfil-alvo informado (`available`) e o que está
  // ligado no agente (`enabled`).
  getTools: async (agentId: string, targetProfileId?: string): Promise<AgentTool[]> => {
    const response = await api.get(`/ai-agents/${agentId}/tools`, { params: { targetProfileId } });
    return response.data;
  },
};
